import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { Plus, Eye, Edit3, Loader2, Folder, CheckCircle2, Clock, XCircle, Search, Trash2, Send, PlayCircle } from 'lucide-react';
import { useReactTable, getCoreRowModel, flexRender, createColumnHelper } from '@tanstack/react-table';
import { useDebounce } from '../hooks/useDebounce.ts';

interface IdeaItem {
  id: string;
  title: string;
  department: string;
  category: string;
  status: string;
  costSaving?: number;
  createdAt: string;
  emailConfirmation?: any;
  misReview?: { misStatus?: 'APPROVED' | 'REJECTED' | null } | null;
}

export function EmployeeDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const handlePageChange = (page: number) => {
    setSearchParams(params => {
      params.set('page', page.toString());
      return params;
    });
  };

  // 1. Fetch dashboard metric summary counts
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['employeeStats'],
    queryFn: async () => {
      const res = await apiClient.get('/ideas/stats');
      return res.data.data;
    },
  });

  // Redirect to new idea if user has absolutely no ideas (not even drafts) - ONLY for Employee users
  useEffect(() => {
    const userRole = localStorage.getItem('userRole') || '';
    const isCommitteeUser = ['COMMITTEE_MEMBER', 'COMMITTEE_HEAD', 'LEADER', 'EXTERNAL_COMMITTEE', 'EXECUTION_COMMITTEE'].includes(userRole);
    if (!isCommitteeUser && !statsLoading && statsData) {
      const totalIdeas = Object.values(statsData).reduce((a: any, b: any) => a + b, 0) as number;
      if (totalIdeas === 0) {
        navigate('/ideas/new', { replace: true });
      }
    }
  }, [statsData, statsLoading, navigate]);

  // 2. Fetch list of employee ideas
  const { data: ideasData, isLoading: ideasLoading } = useQuery({
    queryKey: ['myIdeasDashboard', currentPage, debouncedSearchTerm, statusFilter],
    queryFn: async () => {
      const res = await apiClient.get('/ideas', {
        params: { limit: 10, page: currentPage, search: debouncedSearchTerm || undefined, status: statusFilter || undefined },
      });
      return res.data.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/ideas/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myIdeasDashboard'] });
      queryClient.invalidateQueries({ queryKey: ['employeeStats'] });
    },
  });

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this idea?')) {
      deleteMutation.mutate(id);
    }
  };

  // 3. Columns configuration using TanStack Table
  const columnHelper = createColumnHelper<IdeaItem>();
  const columns = [
    columnHelper.accessor('id', {
      header: 'ID / Department',
      cell: (info) => (
        <div className="flex flex-col gap-1.5 min-w-[130px]">
          <span className="font-semibold text-slate-500 text-xs whitespace-nowrap uppercase">
            CG{info.getValue().substring(0, 7)}
          </span>
          <span className="text-xs font-semibold text-slate-700 leading-tight uppercase">{info.row.original.department}</span>
        </div>
      ),
    }),
    columnHelper.accessor('title', {
      header: 'Idea Title',
      cell: (info) => <div className="min-w-[150px] max-w-[300px] whitespace-normal"><span className="font-semibold text-slate-800 text-sm line-clamp-2" title={info.getValue() as string}>{info.getValue()}</span></div>,
    }),
    columnHelper.accessor('costSaving', {
      header: 'Ind. Savings',
      cell: (info) => {
        const val = info.getValue();
        return val != null ? <span className="text-sm font-semibold text-emerald-600">₹{val.toLocaleString('en-IN')}</span> : <span className="text-slate-400 text-sm">-</span>;
      }
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => {
        const val = info.getValue();
        let displayVal = 'Reviewed';
        let color = 'bg-blue-100 text-blue-700';

        if (val === 'APPROVED' || (info.row.original.emailConfirmation && info.row.original.emailConfirmation.approvedAmount)) {
          displayVal = 'Approved';
          color = 'bg-emerald-100 text-emerald-700';
        } else if (['REJECTED', 'HEAD_REJECTED', 'EXTERNAL_REJECTED'].includes(val)) {
          displayVal = 'Rejected';
          color = 'bg-red-100 text-red-700';
        } else if (val === 'DUPLICATE') {
          displayVal = 'Duplicate';
          color = 'bg-slate-100 text-slate-700';
        } else if (val === 'WORK_IN_PROGRESS') {
          displayVal = 'Shortlisted';
          color = 'bg-cyan-100 text-cyan-700';
        } else if (val === 'SUBMITTED') {
          displayVal = 'Yet to Be Reviewed';
          const createdAt = new Date(info.row.original.createdAt);
          const cutoffDate = new Date('2026-07-19T00:00:00.000+05:30'); // Midnight of 19th July IST
          if (createdAt < cutoffDate) {
            displayVal = 'Reviewed';
            color = 'bg-blue-100 text-blue-700';
          } else {
            color = 'bg-slate-100 text-slate-600';
          }
        }

        return (
          <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${color}`}>
            {displayVal}
          </span>
        );
      },
    }),
    columnHelper.accessor('createdAt', {
      header: 'Submitted On',
      cell: (info) => <span className="text-slate-500 text-sm">{new Date(info.getValue()).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>,
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => (
        <div className="flex items-center justify-end space-x-3">
          {/* <button
            onClick={() => navigate(`/ideas/edit/${info.row.original.id}`)}
            className="text-slate-400 hover:text-blue-600 transition-colors"
            title="Edit Idea"
          >
            <Edit3 className="w-4 h-4" />
          </button> */}
          {info.row.original.misReview?.misStatus !== 'APPROVED' && (
          <button
            onClick={() => handleDelete(info.row.original.id)}
            className="text-slate-400 hover:text-red-600 transition-colors"
            title="Delete Idea"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          )}
          <button
            onClick={() => navigate(`/ideas/${info.row.original.id}?viewAs=employee`)}
            className="text-slate-400 hover:text-blue-600 transition-colors"
            title="View Idea"
          >
            <Eye className="w-4 h-4" />
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: ideasData?.items || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Layout title="Dashboard">
      <div className="mb-6">
        <p className="text-slate-500">Here's what's happening with your ideas.</p>
      </div>

      {/* Overview Cards */}
      {statsLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-4 md:gap-6 mb-8">
          <div
            onClick={() => { setStatusFilter(null); handlePageChange(1); }}
            className={`p-5 rounded-xl border shadow-sm flex flex-col justify-between h-28 cursor-pointer transition-all ${statusFilter === null ? 'bg-blue-50/50 border-blue-400 ring-1 ring-blue-400 shadow-md transform scale-[1.02]' : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-md'}`}
          >
            <div className="flex items-center space-x-3">
              <div className="bg-blue-50 p-2 rounded-lg text-blue-600">
                <Folder className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-600 font-medium">Total Ideas</p>
            </div>
            <p className="text-3xl font-bold text-slate-800">
              {Object.values(statsData || {}).reduce((a: any, b: any) => a + b, 0) as number}
            </p>
          </div>

          <div
            onClick={() => { setStatusFilter('SUBMITTED'); handlePageChange(1); }}
            className={`p-5 rounded-xl border shadow-sm flex flex-col justify-between h-28 cursor-pointer transition-all ${statusFilter === 'SUBMITTED' ? 'bg-slate-50 border-slate-400 ring-1 ring-slate-400 shadow-md transform scale-[1.02]' : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'}`}
          >
            <div className="flex items-center space-x-3">
              <div className="bg-slate-100 p-2 rounded-lg text-slate-600">
                <Send className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-600 font-medium">Yet to Be Reviewed</p>
            </div>
            <p className="text-3xl font-bold text-slate-800">{statsData?.SUBMITTED || 0}</p>
          </div>

          <div
            onClick={() => { setStatusFilter('UNDER_REVIEW,MORE_INFO_REQUESTED,PENDING_HEAD_APPROVAL,PENDING_EXTERNAL_APPROVAL'); handlePageChange(1); }}
            className={`p-5 rounded-xl border shadow-sm flex flex-col justify-between h-28 cursor-pointer transition-all ${statusFilter === 'UNDER_REVIEW,MORE_INFO_REQUESTED,PENDING_HEAD_APPROVAL,PENDING_EXTERNAL_APPROVAL' ? 'bg-amber-50/50 border-amber-400 ring-1 ring-amber-400 shadow-md transform scale-[1.02]' : 'bg-white border-slate-200 hover:border-amber-300 hover:shadow-md'}`}
          >
            <div className="flex items-center space-x-3">
              <div className="bg-amber-50 p-2 rounded-lg text-amber-600">
                <Clock className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-600 font-medium">Reviewed</p>
            </div>
            <p className="text-3xl font-bold text-slate-800">
              {['UNDER_REVIEW', 'MORE_INFO_REQUESTED', 'PENDING_HEAD_APPROVAL', 'PENDING_EXTERNAL_APPROVAL'].reduce((sum, key) => sum + (statsData?.[key] || 0), 0)}
            </p>
          </div>

          <div
            onClick={() => { setStatusFilter('WORK_IN_PROGRESS'); handlePageChange(1); }}
            className={`p-5 rounded-xl border shadow-sm flex flex-col justify-between h-28 cursor-pointer transition-all ${statusFilter === 'WORK_IN_PROGRESS' ? 'bg-cyan-50/50 border-cyan-400 ring-1 ring-cyan-400 shadow-md transform scale-[1.02]' : 'bg-white border-slate-200 hover:border-cyan-300 hover:shadow-md'}`}
          >
            <div className="flex items-center space-x-3">
              <div className="bg-cyan-50 p-2 rounded-lg text-cyan-600">
                <PlayCircle className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-600 font-medium">Shortlisted</p>
            </div>
            <p className="text-3xl font-bold text-slate-800">{statsData?.WORK_IN_PROGRESS || 0}</p>
          </div>

          <div
            onClick={() => { setStatusFilter('APPROVED'); handlePageChange(1); }}
            className={`p-5 rounded-xl border shadow-sm flex flex-col justify-between h-28 cursor-pointer transition-all ${statusFilter === 'APPROVED' ? 'bg-emerald-50/50 border-emerald-400 ring-1 ring-emerald-400 shadow-md transform scale-[1.02]' : 'bg-white border-slate-200 hover:border-emerald-300 hover:shadow-md'}`}
          >
            <div className="flex items-center space-x-3">
              <div className="bg-emerald-50 p-2 rounded-lg text-emerald-600">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-600 font-medium">Approved</p>
            </div>
            <p className="text-3xl font-bold text-slate-800">
              {statsData?.APPROVED || 0}
            </p>
          </div>

          <div
            onClick={() => { setStatusFilter('REJECTED'); handlePageChange(1); }}
            className={`p-5 rounded-xl border shadow-sm flex flex-col justify-between h-28 cursor-pointer transition-all ${statusFilter === 'REJECTED' ? 'bg-red-50/50 border-red-400 ring-1 ring-red-400 shadow-md transform scale-[1.02]' : 'bg-white border-slate-200 hover:border-red-300 hover:shadow-md'}`}
          >
            <div className="flex items-center space-x-3">
              <div className="bg-red-50 p-2 rounded-lg text-red-600">
                <XCircle className="w-5 h-5" />
              </div>
              <p className="text-sm text-slate-600 font-medium">Rejected</p>
            </div>
            <p className="text-3xl font-bold text-slate-800">{statsData?.REJECTED || 0}</p>
          </div>
        </div>
      )}

      {/* Full Ideas Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col mb-8">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center space-x-3 flex-1 max-w-lg">
            <div className="relative flex-1">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400">
                <Search className="w-4 h-4" />
              </span>
              <input
                type="text"
                placeholder="Search ideas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
              />
            </div>
          </div>

          <Link
            to="/ideas/new"
            className="flex items-center justify-center space-x-2 bg-[#1C4ED8] hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            <span>New Idea</span>
          </Link>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {ideasLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50/90 border-b border-slate-200">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${header.id === 'actions' ? 'sticky right-0 bg-slate-50 shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.02)] z-10' : ''}`}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-100">
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-slate-500 text-sm">
                      No ideas found. <Link to="/ideas/new" className="text-blue-600 hover:underline">Submit one!</Link>
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50 transition-colors group">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={`px-6 py-4 align-middle ${cell.column.id === 'actions' ? 'sticky right-0 bg-white shadow-[-4px_0_15px_-3px_rgba(0,0,0,0.02)] group-hover:bg-slate-50 transition-colors z-10' : ''}`}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Toolbar */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            Showing <span className="font-medium text-slate-800">{ideasData?.items?.length || 0}</span> of <span className="font-medium text-slate-800">{ideasData?.meta?.totalCount || 0}</span> ideas
          </p>

          {ideasData?.meta?.totalPages > 1 && (
            <div className="flex items-center space-x-1">
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                className="px-3 py-1.5 border border-slate-300 rounded text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>

              {Array.from({ length: ideasData.meta.totalPages }, (_, i) => i + 1).map((page) => {
                // Show pages around current page
                if (
                  page === 1 ||
                  page === ideasData.meta.totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1.5 border rounded text-sm font-medium transition-colors ${currentPage === page
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-slate-300 text-slate-700 bg-white hover:bg-slate-50'
                        }`}
                    >
                      {page}
                    </button>
                  );
                }

                // Show ellipsis
                if (
                  (page === 2 && currentPage > 3) ||
                  (page === ideasData.meta.totalPages - 1 && currentPage < ideasData.meta.totalPages - 2)
                ) {
                  return <span key={page} className="px-2 text-slate-400">...</span>;
                }

                return null;
              })}

              <button
                disabled={currentPage === ideasData.meta.totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                className="px-3 py-1.5 border border-slate-300 rounded text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
